import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select';
import { Plus, FlaskConical } from 'lucide-react';
import { Checkbox } from './ui/checkbox';
import purchaseService from '../services/purchaseService';
import productService from '../services/productService';
import clientService from '../services/clientService';
import ProductTable from './ProductTable';
import EnhancedProductLookup from './EnhancedProductLookup';
import useUnsavedChangesWarning from '../hooks/useUnsavedChangesWarning';
import UnsavedChangesDialog from './UnsavedChangesDialog';

// Custom Autocomplete component for supplier/client selection
const AutocompleteSelect = ({ options, value, onChange, getOptionLabel, isOptionEqualToValue, label, error, helperText }) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const selectedOption = options.find(opt => isOptionEqualToValue(opt, value));

  useEffect(() => {
    if (selectedOption) {
      setInputValue(getOptionLabel(selectedOption));
    } else if (value) {
      setInputValue(getOptionLabel(value));
    } else {
      setInputValue('');
    }
  }, [selectedOption, value, getOptionLabel]);

  const filteredOptions = options.filter(opt =>
    getOptionLabel(opt).toLowerCase().includes(inputValue.toLowerCase())
  );

  return (
    <div className="relative">
      <Label className="text-sm">{label}</Label>
      <Input
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 200)}
        className={`mt-1 ${error ? 'border-red-500' : ''}`}
      />
      {isOpen && filteredOptions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
          {filteredOptions.map((opt, idx) => (
            <div
              key={idx}
              className="px-3 py-2 hover:bg-muted cursor-pointer"
              onMouseDown={() => {
                onChange(null, opt);
                setIsOpen(false);
              }}
            >
              {getOptionLabel(opt)}
            </div>
          ))}
        </div>
      )}
      {helperText && (
        <p className={`text-xs mt-1 ${error ? 'text-destructive' : 'text-muted-foreground'}`}>{helperText}</p>
      )}
    </div>
  );
};

const PurchaseFormDialog = ({ open, onClose, onSubmit, initialData = null, suppliers = [] }) => {
  const [formData, setFormData] = useState({
    purchase_order_id: `PO-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`,
    supplier_id: '',
    supplier_name: '',
    client_id: '',
    client_ref: '',
    client_name: '',
    expected_delivery_date: '',
    comment: '',
    priority: 'normal',
    currency: 'CNY',
    payment_terms: '',
    tax_rate: 0,
    is_sample: false,
    products: [{
      product_code: '',
      name: '',
      product_name: '',
      part_number: '',
      specification: '',
      material_code: '',
      quantity: 1,
      unit_cost: 0,
      line_total: 0,
      notes: ''
    }]
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [suppliersData, setSuppliersData] = useState(suppliers || []);
  const [clientsData, setClientsData] = useState([]);
  const [productDialogOpen, setProductDialogOpen] = useState(false);

  // Track if form has been initialized to prevent re-initialization on parent re-renders
  const initializedRef = useRef(false);
  const prevOpenRef = useRef(open);

  // Initialize unsaved changes warning hook
  const unsavedChangesWarning = useUnsavedChangesWarning(formData, open);

  // Load suppliers if not provided - only on mount
  useEffect(() => {
    if (!suppliers || suppliers.length === 0) {
      loadSuppliers();
    }
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSuppliers = async () => {
    try {
      const response = await purchaseService.getSuppliers(1, 100);
      setSuppliersData(Array.isArray(response) ? response : response.suppliers || []);
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  };

  const loadClients = async () => {
    try {
      const response = await clientService.getClients(1, 200);
      setClientsData(Array.isArray(response) ? response : response.clients || []);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  useEffect(() => {
    // Track previous open state for initialization logic
    prevOpenRef.current = open;

    // Reset initialization flag when dialog closes
    if (!open) {
      initializedRef.current = false;
      return;
    }

    // Skip if already initialized while dialog is open
    if (initializedRef.current) {
      return;
    }

    initializedRef.current = true;

    if (initialData) {
      const data = { ...initialData };

      if (!data.products || data.products.length === 0) {
        data.products = [{
          product_code: '',
          product_name: '',
          part_number: '',
          specification: '',
          material_code: '',
          quantity: 1,
          unit_cost: 0,
          line_total: 0,
          notes: ''
        }];
      }

      if (data.expected_delivery_date) {
        data.expected_delivery_date = new Date(data.expected_delivery_date).toISOString().split('T')[0];
      }

      if (!data.purchase_order_id) {
        data.purchase_order_id = `PO-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`;
      }

      setFormData(data);
      setTimeout(() => {
        unsavedChangesWarning.updateInitialState(data);
      }, 100);
    } else {
      setFormData({
        purchase_order_id: `PO-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`,
        supplier_id: '',
        supplier_name: '',
        client_id: '',
        client_ref: '',
        client_name: '',
        expected_delivery_date: '',
        comment: '',
        priority: 'normal',
        currency: 'CNY',
        payment_terms: '',
        tax_rate: 0,
        is_sample: false,
        products: [{
          product_code: '',
          product_name: '',
          part_number: '',
          specification: '',
          material_code: '',
          quantity: 1,
          unit_cost: 0,
          line_total: 0,
          notes: ''
        }]
      });
    }
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, open]);

  useEffect(() => {
    const ensureSupplierLoaded = async () => {
      const id = formData?.supplier_id;
      if (!id) return;
      const exists = suppliersData.some(s => (s._id || s.id) === id);
      if (exists) return;
      try {
        const resp = await purchaseService.getSupplier(id);
        const supplier = resp?.supplier || resp;
        if (supplier && (supplier._id || supplier.id)) {
          setSuppliersData(prev => {
            const alreadyExists = prev.some(s => (s._id || s.id) === (supplier._id || supplier.id));
            if (alreadyExists) return prev;
            return [supplier, ...prev];
          });
        }
      } catch (e) {
        console.warn('Failed to load supplier by ID:', id, e);
      }
    };
    ensureSupplierLoaded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.supplier_id]);

  // Ensure client is loaded for edit mode
  useEffect(() => {
    const ensureClientLoaded = async () => {
      const id = formData?.client_ref || formData?.client_id;
      if (!id) return;
      const exists = clientsData.some(c => (c._id || c.id) === id);
      if (exists) return;
      try {
        const resp = await clientService.getClient(id);
        const client = resp?.client || resp;
        if (client && (client._id || client.id)) {
          setClientsData(prev => {
            const alreadyExists = prev.some(c => (c._id || c.id) === (client._id || client.id));
            if (alreadyExists) return prev;
            return [client, ...prev];
          });
        }
      } catch (e) {
        console.warn('Failed to load client by ID:', id, e);
      }
    };
    ensureClientLoaded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.client_ref, formData?.client_id]);

  const handleSupplierChange = (event, newValue) => {
    if (newValue) {
      setFormData(prev => ({
        ...prev,
        supplier_id: newValue._id,
        supplier_name: newValue.supplier_name
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        supplier_id: '',
        supplier_name: ''
      }));
    }
    if (errors.supplier_id) {
      setErrors(prev => ({ ...prev, supplier_id: null }));
    }
  };

  const handleClientChange = (event, newValue) => {
    if (newValue) {
      const clientObjectId = newValue._id || newValue.id;
      setFormData(prev => ({
        ...prev,
        client_id: newValue.client_id || newValue.client_code || clientObjectId,
        client_ref: clientObjectId,
        client_name: newValue.client_name || newValue.name
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        client_id: '',
        client_ref: '',
        client_name: ''
      }));
    }
    if (errors.client_id) {
      setErrors(prev => ({ ...prev, client_id: null }));
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleAddSelectedProducts = (selectedProducts) => {
    console.log('Adding selected products:', selectedProducts);

    setFormData(prev => {
      let currentProducts = [...prev.products];

      if (currentProducts.length === 1 && !currentProducts[0].product_name && !currentProducts[0].product_code && !currentProducts[0].name) {
        currentProducts = [];
      }

      selectedProducts.forEach(selected => {
        // Check for existing product with same product_id, specification, AND client_ref_number
        // Products with same product_id but different specification are treated as separate line items
        const existingIndex = currentProducts.findIndex(p =>
          p.product_id === selected.product_id &&
          p.specification === selected.specification &&
          (p.client_ref_number || '') === (selected.client_ref_number || '')
        );

        if (existingIndex > -1) {
          currentProducts[existingIndex].quantity = (currentProducts[existingIndex].quantity || 0) + 1;
          currentProducts[existingIndex].line_total = currentProducts[existingIndex].quantity * currentProducts[existingIndex].unit_cost;
        } else {
          const newProduct = {
            _id: selected._id,
            product_id: selected.product_id,
            product_code: selected.product_code || selected.product_id,
            name: selected.name || selected.product_name || '',
            product_name: selected.name || selected.product_name || '',
            part_number: selected.part_number || '',
            specification: selected.specification || '',
            material_code: selected.material_code || '',
            category: selected.category || '',
            unit: selected.unit || '支',
            material: selected.material || '',
            coating: selected.coating || '',
            tip_diameter: selected.tip_diameter || '',
            total_length: selected.total_length || '',
            degree: selected.degree || '',
            shank_diameter: selected.shank_diameter || '',
            tolerance: selected.tolerance || '',
            blade_type: selected.blade_type || '',
            guide_pillar_length: selected.guide_pillar_length || '',
            guide_pillar_tolerance: selected.guide_pillar_tolerance || '',
            marking: selected.marking || '',
            client_ref_number: selected.client_ref_number || '',
            quantity: 1,
            unit_cost: selected.price || 0,
            line_total: (selected.price || 0) * 1,
            notes: ''
          };
          currentProducts.push(newProduct);
        }
      });

      return { ...prev, products: currentProducts };
    });
    setProductDialogOpen(false);
  };

  const handleRemoveProduct = (index) => {
    if (formData.products.length > 1) {
      setFormData(prev => ({
        ...prev,
        products: prev.products.filter((_, i) => i !== index)
      }));
    }
  };

  const calculateTotals = () => {
    const subtotal = formData.products.reduce((sum, product) => sum + (product.line_total || 0), 0);
    const totalProducts = formData.products.reduce((sum, product) => sum + (parseFloat(product.quantity) || 0), 0);
    const taxAmount = subtotal * (formData.tax_rate || 0);
    const totalCost = subtotal + taxAmount;

    return { subtotal, totalProducts, taxAmount, totalCost };
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.supplier_id) {
      newErrors.supplier_id = '请选择供应商';
    }

    if (!formData.client_id || !formData.client_ref) {
      newErrors.client_id = '请选择客户';
    }

    formData.products.forEach((product, index) => {
      const productName = product.name || product.product_name;
      if (!productName || productName.trim() === '') {
        newErrors[`product_name_${index}`] = '请填写产品名称';
      }
      if (!product.quantity || product.quantity <= 0) {
        newErrors[`product_quantity_${index}`] = '数量必须大于0';
      }

    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setErrors(prev => ({
        ...prev,
        submit: '请填写所有必填字段'
      }));
      return;
    }

    setLoading(true);
    unsavedChangesWarning.setIsSubmitting(true);
    try {
      const { subtotal, totalProducts, taxAmount, totalCost } = calculateTotals();

      const submitData = {
        ...formData,
        total_products: totalProducts,
        subtotal: subtotal,
        tax_amount: taxAmount,
        total_cost: totalCost,
        tax_rate: formData.tax_rate <= 1 ? formData.tax_rate : formData.tax_rate / 100
      };

      await onSubmit(submitData);
      unsavedChangesWarning.resetDirty();
      handleClose();
    } catch (error) {
      console.error('Error submitting purchase order:', error);
      setErrors({ submit: error.response?.data?.error || 'Failed to save purchase order' });
      unsavedChangesWarning.setIsSubmitting(false);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      unsavedChangesWarning.checkUnsavedChanges(() => {
        setFormData({
          supplier_id: '',
          supplier_name: '',
          client_id: '',
          client_ref: '',
          client_name: '',
          expected_delivery_date: '',
          comment: '',
          priority: 'normal',
          currency: 'CNY',
          payment_terms: '',
          tax_rate: 0,
          is_sample: false,
          products: [{
            product_code: '',
            product_name: '',
            part_number: '',
            specification: '',
            material_code: '',
            quantity: 1,
            unit_cost: 0,
            line_total: 0,
            notes: ''
          }]
        });
        setErrors({});
        onClose();
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {initialData ? '编辑采购单' : '新建采购单'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {errors.submit && (
            <Alert variant="destructive">
              <AlertDescription>{errors.submit}</AlertDescription>
            </Alert>
          )}

          {/* Basic Information */}
          <div>
            <h3 className="text-base font-semibold mb-4">基本信息</h3>
            <Separator className="mb-4" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>采购单编号</Label>
                <Input
                  value={formData.purchase_order_id}
                  onChange={(e) => handleFieldChange('purchase_order_id', e.target.value)}
                  placeholder="输入采购单编号"
                />
                <p className="text-xs text-muted-foreground">可自定义采购单编号</p>
              </div>

              <AutocompleteSelect
                options={suppliersData}
                value={
                  suppliersData.find(s => (s._id || s.id) === formData.supplier_id)
                  || (formData.supplier_id ? { _id: formData.supplier_id, supplier_name: formData.supplier_name || '' } : null)
                }
                onChange={handleSupplierChange}
                getOptionLabel={(option) => option?.supplier_name || ''}
                isOptionEqualToValue={(option, value) => (
                  (option?._id || option?.id) === (value?._id || value?.id)
                )}
                label="供应商 *"
                error={!!errors.supplier_id}
                helperText={errors.supplier_id}
              />

              <AutocompleteSelect
                options={clientsData}
                value={
                  clientsData.find(c => (c._id || c.id) === formData.client_ref || (c._id || c.id) === formData.client_id)
                  || (formData.client_ref ? { _id: formData.client_ref, client_name: formData.client_name || '', name: formData.client_name || '' } : null)
                }
                onChange={handleClientChange}
                getOptionLabel={(option) => `${option?.client_name || option?.name || ''} (${option?.client_id || option?.client_code || ''})`}
                isOptionEqualToValue={(option, value) => (option?._id || option?.id) === (value?._id || value?.id)}
                label="客户 *"
                error={!!errors.client_id}
                helperText={errors.client_id}
              />

              <div className="space-y-2">
                <Label>预计交货日期</Label>
                <Input
                  type="date"
                  value={formData.expected_delivery_date}
                  onChange={(e) => handleFieldChange('expected_delivery_date', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>优先级</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => handleFieldChange('priority', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">低</SelectItem>
                    <SelectItem value="normal">普通</SelectItem>
                    <SelectItem value="high">高</SelectItem>
                    <SelectItem value="urgent">紧急</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>币种</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) => handleFieldChange('currency', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CNY">人民币 (CNY)</SelectItem>
                    <SelectItem value="USD">美元 (USD)</SelectItem>
                    <SelectItem value="EUR">欧元 (EUR)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>税率 (%)</Label>
                <Input
                  type="number"
                  value={formData.tax_rate * 100}
                  onChange={(e) => handleFieldChange('tax_rate', parseFloat(e.target.value) / 100 || 0)}
                  min={0}
                  max={100}
                  step={0.1}
                />
              </div>

              <div className="space-y-2">
                <Label>样品单</Label>
                <div className="flex items-center gap-2 h-10">
                  <Checkbox
                    id="is_sample"
                    checked={formData.is_sample}
                    onCheckedChange={(checked) => handleFieldChange('is_sample', checked)}
                  />
                  <label htmlFor="is_sample" className="text-sm text-muted-foreground flex items-center gap-1 cursor-pointer">
                    <FlaskConical className="h-4 w-4 text-purple-500" />
                    标记为样品订单
                  </label>
                </div>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>付款条款</Label>
                <Input
                  value={formData.payment_terms}
                  onChange={(e) => handleFieldChange('payment_terms', e.target.value)}
                  placeholder="例如: 30天付款"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>备注</Label>
                <Textarea
                  value={formData.comment}
                  onChange={(e) => handleFieldChange('comment', e.target.value)}
                  rows={2}
                />
              </div>
            </div>
          </div>

          {/* Products Table */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base font-semibold">产品清单</h3>
              <Button
                variant="outline"
                onClick={() => setProductDialogOpen(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                添加产品
              </Button>
            </div>

            <ProductTable
              products={formData.products}
              view="purchase"
              isSample={formData.is_sample}
              onEdit={(product) => {
                console.log('Edit product:', product);
              }}
              onDelete={(productId) => {
                const index = formData.products.findIndex(p => p._id === productId || p.id === productId);
                if (index !== -1 && formData.products.length > 1) {
                  handleRemoveProduct(index);
                }
              }}
              onPriceChange={(index, newPrice) => {
                const updatedProducts = [...formData.products];
                updatedProducts[index] = { ...updatedProducts[index], price: newPrice };
                setFormData(prev => ({ ...prev, products: updatedProducts }));
              }}
              onQuantityChange={(index, newQuantity) => {
                const updatedProducts = [...formData.products];
                updatedProducts[index] = { ...updatedProducts[index], quantity: newQuantity };
                setFormData(prev => ({ ...prev, products: updatedProducts }));
              }}
              onPriceLookup={async (index, product) => {
                console.log('Price lookup for product:', product);
                try {
                  if (product.product_id) {
                    const data = await productService.searchProductPriceHistory(product.product_id);
                    const priceHistory = data.price_history || [];
                    if (priceHistory.length > 0) {
                      const prices = priceHistory.map(h => `¥${h.price}`).join(', ');
                      alert(`价格历史 (${product.name}): ${prices}`);
                    } else {
                      alert('未找到此产品的价格历史记录');
                    }
                  } else {
                    alert('产品缺少产品编号，无法查询价格历史');
                  }
                } catch (error) {
                  console.error('Price lookup error:', error);
                  alert('查询价格历史时发生错误');
                }
              }}
              onReorder={(reorderedProducts) => {
                setFormData(prev => ({ ...prev, products: reorderedProducts }));
              }}
              readOnly={false}
              title="采购订单产品列表"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            取消
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? '保存中...' : (initialData ? '保存' : '创建')}
          </Button>
        </DialogFooter>

        <EnhancedProductLookup
          open={productDialogOpen}
          onClose={() => setProductDialogOpen(false)}
          onAddProducts={handleAddSelectedProducts}
          clientName={formData.supplier_name}
          currentQuotationItems={formData.products}
        />

        {/* Unsaved Changes Warning Dialog */}
        <UnsavedChangesDialog
          open={unsavedChangesWarning.showDialog}
          onSaveAndLeave={() => unsavedChangesWarning.handleSaveAndLeave(handleSubmit)}
          onDiscardChanges={unsavedChangesWarning.handleDiscardChanges}
          onCancel={unsavedChangesWarning.handleCancel}
          title="未保存的采购单"
          message="您有未保存的采购单更改。关闭此窗口将丢失这些更改。"
        />
      </DialogContent>
    </Dialog>
  );
};

export default PurchaseFormDialog;
